import { useCallback, useEffect, useRef, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { Link, useParams } from 'react-router-dom';
import { useBusinessId, useTenantParams } from '@/hooks/use-tenant-params';
import { ArrowUpRight, ExternalLink, Loader2, Send, ShieldCheck, Star } from 'lucide-react';
import { EmptyState } from '@/components/empty-state';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { Skeleton } from '@/components/skeleton';
import { LockedPanel } from '@/components/gbp/locked-panel';
import { NotConnectedPanel } from '@/components/gbp/not-connected-panel';
import { LocationPicker } from '@/components/gbp/location-picker';
import { GbpReviewsList } from '@/components/gbp/reviews-list';
import { PartErrorBanner, StaleStamp } from '@/components/gbp/part-status';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { useGbpData, invalidateGbp } from '@/hooks/use-gbp-data';
import { api, type ApiError } from '@/lib/api';
import { toReviewView } from '@/lib/gbp-adapters';
import { cn } from '@/lib/utils';
import { businessRoutes } from '@/lib/route-map';
import type {
  CreateReviewResponse,
  EffectiveTier,
  GbpResponse,
  ReviewChannel,
  ReviewRequestRow,
  ReviewsResponse,
  ReviewState,
  SendReviewResponse,
} from '@/lib/types';

// =====================================================================
// Reviews — "/app/b/:siteId/reputation/reviews" (WP1 Task 9). Reads real
// Google reviews + AI reply drafts through the shared, single-flight
// `useGbpData` hook (Task 8) — the section moved here verbatim from
// components/gbp/connected-panels.tsx (deleted in Task 8's a81ebcf), which
// itself is the reviews half of the pre-Task-8 site-gbp.tsx.
//
// Below the GBP reviews section sits "Ask for reviews" — the review-REQUEST
// velocity engine, moved wholesale from the old flat site-reviews.tsx
// (Phase D). Operator decision D1 (locked, brief's Conflicts note): the
// spec's reputation group never named this feature on its own, so it folds
// in here as a section rather than getting its own nav item — its own
// Business/Growth gate stays inline exactly as it was — the shared
// UpgradeGate pattern (WP1 Task 12) was never wired here; revisit only if
// this section joins that shared pattern.
// =====================================================================

type ConnectedGbp = Extract<GbpResponse, { state: 'connected' }>;

export function Reviews() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();
  const gbp = useGbpData(businessId, siteId ?? '');

  const [announcement, setAnnouncement] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const focusAfterLoad = useRef(false);

  // The old `/api/sites/:id` fetch here existed only to put the business
  // name in the <h1> — the h1 is now the PAGE name (design audit B1), so
  // this screen no longer loads the site record at all.

  // Same live-region + focus-move contract as google-profile.tsx: a
  // content-replacing success (reply posted, location picked) announces
  // itself and moves focus onto the refreshed body.
  const handleChanged = useCallback(
    (successAnnouncement?: string) => {
      if (!businessId || !siteId) return;
      invalidateGbp(businessId, siteId);
      if (successAnnouncement) {
        setAnnouncement(successAnnouncement);
        focusAfterLoad.current = true;
      }
    },
    [businessId, siteId],
  );

  useEffect(() => {
    if (!focusAfterLoad.current || gbp.kind !== 'ready') return;
    focusAfterLoad.current = false;
    bodyRef.current?.focus();
  }, [gbp]);

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Reputation"
        title="Reviews & replies"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready' || !siteId) return null;

  const tier = meState.me.usage.tier;

  // Design audit T8 (A11, one primary per screen). The Google panel above
  // renders its OWN filled CTA in exactly two states — "Connect your Google
  // Business Profile" (not connected) and "Upgrade plan" (locked) — and both
  // are the bigger ask: without a connected profile there is nowhere for a
  // review to land. While one of those is on screen it IS the screen's single
  // primary, and the "Ask for reviews" section below steps down to outline.
  // Connected (or mid-location-pick, which is a row list with no filled
  // button), "Send review request" is the primary again.
  //
  // T14: `loading` counts too. It resolves into one of those two states often
  // enough that filling the button first and un-filling it a moment later
  // made the screen flicker its own emphasis — and the honest reading of "we
  // don't know yet" is not "this is the thing to press". An `error` does NOT
  // count: the panel renders no CTA at all in that state, so the ask below is
  // genuinely the only thing left to do.
  const gbpOwnsPrimary =
    gbp.kind === 'loading' ||
    (gbp.kind === 'ready' &&
      (gbp.data.state === 'locked' || gbp.data.state === 'not_connected'));

  return (
    <PageContainer>
      <PageHeader eyebrow="Reputation" title="Reviews & replies" />

      <p aria-live="polite" role="status" className="sr-only">
        {announcement ?? ''}
      </p>

      {gbp.kind === 'error' ? (
        <div className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg" role="alert">
          {gbp.message}
        </div>
      ) : null}

      <div ref={bodyRef} tabIndex={-1} className="flex flex-col gap-6 focus:outline-none">
        {gbp.kind === 'loading' ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        ) : gbp.kind === 'error' ? null : gbp.data.state === 'locked' ? (
          <LockedPanel siteId={siteId} hasConnection={gbp.data.hasConnection} />
        ) : gbp.data.state === 'not_connected' ? (
          <NotConnectedPanel siteId={siteId} />
        ) : gbp.data.state === 'pending_selection' ? (
          <LocationPicker candidates={gbp.data.candidates} onChanged={handleChanged} />
        ) : (
          <ReviewsSection siteId={siteId} data={gbp.data} onChanged={handleChanged} />
        )}
      </div>

      <AskForReviewsSection siteId={siteId} tier={tier} gbpOwnsPrimary={gbpOwnsPrimary} />
    </PageContainer>
  );
}

// Recent-reviews section — moved verbatim (layout, copy, gating) from
// components/gbp/connected-panels.tsx's reviews `<section>`, minus the
// location/insights/posts/Q&A/disconnect material that stayed on the
// Google Profile screen (Task 8).
function ReviewsSection({
  siteId,
  data,
  onChanged,
}: {
  siteId: string;
  data: ConnectedGbp;
  onChanged: (announcement?: string) => void;
}) {
  const locationData = data.location.data;
  const reviews = data.reviews;
  const reviewsData = reviews.data;

  return (
    <section className="rounded-lg border border-border-subtle bg-card p-6">
      <header className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Recent reviews</h2>
          {reviewsData ? (
            <p className="mt-1 text-sm text-text-secondary">
              {reviewsData.totalReviewCount.toLocaleString()} reviews
              {reviewsData.averageRating !== null
                ? ` · average ${reviewsData.averageRating.toFixed(1)} ★`
                : ''}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-3">
          {reviews.ok === false ? <StaleStamp fetchedAt={reviews.fetchedAt} /> : null}
          {locationData?.placeId ? (
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(locationData.title)}#lrd=${locationData.placeId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-text-secondary hover:text-foreground"
            >
              All reviews on Google
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      </header>

      {reviews.ok === false ? (
        <PartErrorBanner
          siteId={siteId}
          what="your reviews"
          error={reviews.error}
          hasStaleData={!!reviews.data}
        />
      ) : null}

      {reviewsData ? (
        // reviewsData is only defined here with real data to show — a fresh
        // ok:true fetch or a stale-but-present envelope on a failed refetch.
        // An empty `reviews` array in either case is a genuine, known zero,
        // so GbpReviewsList's own "No reviews yet." is honest.
        <GbpReviewsList
          reviews={reviewsData.reviews.map(toReviewView)}
          siteId={siteId}
          canReply={data.canReply && data.canWrite}
          onChanged={onChanged}
        />
      ) : (
        // reviews.data is undefined: the part has never succeeded even
        // once. Do NOT fall back to an empty array — that would assert a
        // zero we do not actually know, directly under the error banner
        // above that just said the fetch failed.
        <p className="text-sm text-text-tertiary">Reviews unavailable right now.</p>
      )}
    </section>
  );
}

// =====================================================================
// Ask for reviews — the review-velocity engine (Phase D), moved wholesale
// from the old flat site-reviews.tsx. Compliant by design: ask EVERY
// customer the same way (no gating to who'll leave a good one), reward the
// ASK not the outcome, and capture TCPA consent before any send. senderZ
// delivery is DISABLED-SAFE — a send with the provider unwired parks the request
// as "queued" and the UI says it was saved but NOT sent. Nothing re-sends a queued
// request later (PB-004), so no copy may promise that it "will go out". Consumes
// /api/reviews/*. Gate unchanged from the pre-move screen (Business/Growth
// inline upgrade card) — stays as-is; revisit only if this section joins
// the shared UpgradeGate pattern (WP1 Task 12).
// =====================================================================

const STATE_META: Record<ReviewState, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'text-text-secondary' },
  // Not "Queued": a queued request never moves on by itself (unwired provider,
  // unconfirmed delivery, or an unsaved status) — PB-004.
  queued: { label: 'On hold', cls: 'text-warning-fg' },
  sent: { label: 'Asked', cls: 'text-brand-700' },
  clicked: { label: 'Opened', cls: 'text-brand-700' },
  left: { label: 'Reviewed', cls: 'text-success-fg' },
  failed: { label: 'Failed', cls: 'text-danger-fg' },
  suppressed: { label: 'Stopped', cls: 'text-text-tertiary' },
};

const CHANNEL_OPTIONS: ReadonlyArray<{ value: ReviewChannel; label: string }> = [
  { value: 'sms', label: 'Text' },
  { value: 'email', label: 'Email' },
];

function consentTextFor(channel: ReviewChannel): string {
  const how = channel === 'sms' ? 'a text message' : 'an email';
  return `This customer agreed to receive a one-time review request from us by ${how}.`;
}

const SEND_COPY = {
  sent: 'Sent. We asked them the same compliant way we ask everyone.',
  notSent:
    'Saved, but not sent. Sending isn’t set up yet, so this request hasn’t gone out and won’t go out on its own.',
  unrecorded:
    'This request needs a manual check. The message was accepted for delivery, but we couldn’t save its status. We won’t send it again.',
  uncertain:
    'We couldn’t confirm this message was delivered. Your customer may already have received it, so we won’t resend it automatically.',
  // PB-005: no Google review link known for this location — the server refused before sending anything.
  noReviewLink:
    'Not sent. We don’t have your Google review link yet, so the email would have nowhere to send your customer. Connect your Google Profile, then send again.',
} as const;

function sendErrorMessage(e: ApiError): string {
  const code = e.body && typeof e.body === 'object' && 'error' in e.body ? (e.body as { error: unknown }).error : null;
  if (code === 'delivery_uncertain') return SEND_COPY.uncertain;
  if (code === 'review_link_missing') return SEND_COPY.noReviewLink;
  switch (e.status) {
    case 409:
      return 'That request is already on its way or finished.';
    case 422:
      return "We can't send without the customer's consent on record. Tick the consent box and try again.";
    case 403:
      return 'Review requests are on Business and Growth.';
    default:
      return e instanceof Error ? e.message : 'Could not send. Try again.';
  }
}

function AskForReviewsSection({
  siteId,
  tier,
  gbpOwnsPrimary,
}: {
  siteId: string;
  tier: EffectiveTier;
  /** The Google panel above is showing its own filled CTA — see Reviews(). */
  gbpOwnsPrimary: boolean;
}) {
  // Canonical tenant URLs carry the Business id; this is only rendered inside
  // one, so it comes from the router rather than being threaded as a prop.
  const businessId = useBusinessId() ?? '';
  const gated = tier !== 'business' && tier !== 'growth';

  const [data, setData] = useState<ReviewsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    if (gated) return;
    void api
      .get<ReviewsResponse>(locationApi(businessId, siteId, 'reviews'))
      .then((res) => setData(res))
      .catch((err: ApiError) => {
        if (err.status === 403) return;
        setError(err instanceof Error ? err.message : 'Could not load your review requests.');
      });
  }, [businessId, siteId, gated]);

  useEffect(() => load(), [load]);

  async function send(reqId: string) {
    setSendingId(reqId);
    setNote(null);
    setError(null);
    try {
      const res = await api.post<SendReviewResponse>(locationApi(businessId, siteId, `reviews/${reqId}/send`), {});
      if (res.dormant) {
        setNote(SEND_COPY.notSent);
      } else if (res.recorded === false) {
        // The provider took it but the status was not saved: never show this as a clean send.
        setError(SEND_COPY.unrecorded);
      } else {
        setNote(SEND_COPY.sent);
      }
      load();
    } catch (err) {
      const e = err as ApiError;
      setError(sendErrorMessage(e));
    } finally {
      setSendingId(null);
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-semibold tracking-tight">Ask for reviews</h2>
        <p className="mt-1 max-w-prose text-sm text-text-secondary">
          Ask your customers for an honest Google review. We ask everyone the same way — never only
          the ones likely to rave — because that’s what keeps your listing safe.
        </p>
      </header>

      {gated ? (
        <div className="flex flex-col items-start gap-4 rounded-xl border border-border-subtle bg-card p-8">
          <div className="max-w-prose">
            <h3 className="text-xl font-semibold tracking-tight">Turn happy customers into reviews</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Send compliant, one-tap review requests and watch your Google reviews grow. Included on
              Business and Growth.
            </p>
          </div>
          {/* A Starter-tier owner sees the locked Google panel's "Upgrade
              plan" too — two identical filled CTAs, one screen. The panel
              above keeps the primary; this one repeats the offer in outline. */}
          <Link
            to={businessRoutes.billing(businessId)}
            className={cn(buttonVariants(gbpOwnsPrimary ? { variant: 'outline' } : undefined))}
          >
            Upgrade plan
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <>
          <VelocityBlock data={data} />

          <RequestForm
            siteId={siteId}
            onCreated={(reqId) => void send(reqId)}
            busy={sendingId !== null}
            primary={!gbpOwnsPrimary}
          />

          {note ? (
            <p className="rounded-lg border border-border-subtle bg-surface-sunken p-3 text-sm text-text-secondary">
              {note}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-danger-fg">
              {error}
            </p>
          ) : null}

          <RequestList data={data} sendingId={sendingId} onSend={(reqId) => void send(reqId)} />
        </>
      )}
    </section>
  );
}

// Reward-the-ask velocity: lead with how many asks went out, not reviews received.
function VelocityBlock({ data }: { data: ReviewsResponse | null }) {
  if (data === null) return <Skeleton className="h-24 w-full rounded-xl" />;
  const left = data.counts.left ?? 0;
  return (
    <section className="flex flex-wrap items-end gap-x-8 gap-y-3 rounded-xl border border-border-subtle bg-card p-6">
      <div className="flex flex-col gap-1">
        <p className="eyebrow">Review asks sent</p>
        <span className="text-4xl font-bold leading-none tracking-tight text-text-primary tabular-nums">
          {data.asksSent}
        </span>
      </div>
      {left > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="eyebrow">Reviews left</p>
          <span className="text-4xl font-bold leading-none tracking-tight text-success-fg tabular-nums">
            {left}
          </span>
        </div>
      ) : null}
      <p className="flex items-start gap-1.5 text-sm leading-relaxed text-text-secondary">
        <Star className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" />
        <span>We count the ask, not the outcome — asking consistently is the part you control.</span>
      </p>
    </section>
  );
}

function RequestForm({
  siteId,
  onCreated,
  busy,
  primary,
}: {
  siteId: string;
  onCreated: (reqId: string) => void;
  busy: boolean;
  /**
   * Whether "Send review request" is the screen's one filled button. False
   * while the Google panel above is showing its own CTA (design audit T8).
   */
  primary: boolean;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [customerName, setCustomerName] = useState('');
  const [channel, setChannel] = useState<ReviewChannel>('email');
  const [contact, setContact] = useState('');
  const [consent, setConsent] = useState(false);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (contact.trim() === '' || !consent) return;
    setCreating(true);
    setErr(null);
    try {
      // PB-003: the tenant is the canonical URL's alone — no business/location id in the body.
      const res = await api.post<CreateReviewResponse>(locationApi(businessId, siteId, 'reviews/request'), {
        channel,
        contact: contact.trim(),
        ...(customerName.trim() !== '' ? { customerName: customerName.trim() } : {}),
        consentText: consentTextFor(channel),
        tcpaConsent: true,
      });
      // Clear the form, then hand the new request to the parent to send.
      setCustomerName('');
      setContact('');
      setConsent(false);
      onCreated(res.id);
    } catch (e) {
      const ae = e as ApiError;
      setErr(ae.status === 400 ? 'Check the name and contact, then try again.' : ae instanceof Error ? ae.message : 'Could not save. Try again.');
    } finally {
      setCreating(false);
    }
  }

  const disabled = creating || busy;
  return (
    <section className="flex flex-col gap-4 rounded-xl border border-border-subtle bg-card p-6">
      <h3 className="text-base font-semibold tracking-tight">Ask a customer</h3>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Input
          placeholder="Customer name (optional)"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          disabled={disabled}
          aria-label="Customer name"
          className="sm:flex-1"
        />
        {/* Design audit T7: this was a hand-rolled pair whose selected half
            was filled paprika (`bg-primary`) — the screen's one primary
            colour spent on a toggle. It is the shared SegmentedControl now:
            neutral raised pill for "selected", and a real radiogroup. */}
        <SegmentedControl<ReviewChannel>
          ariaLabel="How to ask"
          value={channel}
          options={CHANNEL_OPTIONS}
          onChange={setChannel}
          disabled={disabled}
          className="shrink-0"
        />
      </div>

      <Input
        type={channel === 'sms' ? 'tel' : 'email'}
        inputMode={channel === 'sms' ? 'tel' : 'email'}
        placeholder={channel === 'sms' ? 'Mobile number, e.g. +1 619 555 0100' : 'name@example.com'}
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        disabled={disabled}
        aria-label={channel === 'sms' ? 'Mobile number' : 'Email address'}
      />

      <label className="flex min-h-6 items-start gap-2.5 pointer-coarse:min-h-11 text-sm leading-relaxed text-text-secondary">
        <input
          type="checkbox"
          checked={consent}
          disabled={disabled}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-px h-6 w-6 shrink-0 rounded border-input accent-brand-500"
        />
        <span className="inline-flex items-start gap-1.5">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary" />
          {consentTextFor(channel)} (Required — the law needs their OK before we message them.)
        </span>
      </label>

      {err ? (
        <p role="alert" className="text-sm text-danger-fg">
          {err}
        </p>
      ) : null}

      <div>
        <Button
          type="button"
          variant={primary ? 'default' : 'outline'}
          disabled={disabled || contact.trim() === '' || !consent}
          onClick={() => void submit()}
        >
          {creating || busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send review request
        </Button>
      </div>
    </section>
  );
}

function RequestList({
  data,
  sendingId,
  onSend,
}: {
  data: ReviewsResponse | null;
  sendingId: string | null;
  onSend: (reqId: string) => void;
}) {
  if (data === null) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-16 w-full rounded-xl" />
        <Skeleton className="h-16 w-full rounded-xl" />
      </div>
    );
  }
  if (data.requests.length === 0) {
    return (
      <EmptyState
        title="No review requests yet"
        description="Ask your first customer above — every request goes out the same way, to everyone."
      />
    );
  }
  return (
    <ul className="flex flex-col gap-2">
      {data.requests.map((r) => (
        <RequestRow key={r.id} req={r} sending={sendingId === r.id} onSend={() => onSend(r.id)} />
      ))}
    </ul>
  );
}

function RequestRow({
  req,
  sending,
  onSend,
}: {
  req: ReviewRequestRow;
  sending: boolean;
  onSend: () => void;
}) {
  const meta = STATE_META[req.state];
  // Only draft and failed can be (re)sent — queued is in flight or on hold; sent/
  // clicked/left/suppressed are done or terminal.
  const canSend = req.state === 'draft' || req.state === 'failed';
  const when = req.sentAt ?? req.createdAt;
  return (
    <li className="flex items-center justify-between gap-3 rounded-xl border border-border-subtle bg-card p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-foreground">{req.customerName || 'Customer'}</span>
          <span className="text-xs text-text-tertiary">· {req.channel === 'sms' ? 'Text' : 'Email'}</span>
          <span className={cn('text-xs font-medium', meta.cls)}>· {meta.label}</span>
        </div>
        <p className="mt-0.5 text-xs text-text-tertiary">{new Date(when).toLocaleDateString()}</p>
      </div>
      {canSend ? (
        <Button type="button" variant="outline" size="sm" disabled={sending} onClick={onSend}>
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {req.state === 'failed' ? 'Retry' : 'Send'}
        </Button>
      ) : null}
    </li>
  );
}

export default Reviews;
